import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { PORTFOLIO } from "@/constants/testIds";
import { toast } from "sonner";
import {
  Award, Trophy, Flame, Zap, Sparkles, Share2, ArrowLeft, Copy,
} from "lucide-react";
import { CertificateQR, VerifiedBadge } from "@/components/CertificateShare";

function PortfolioPrivacyControls({ initial, onSaved }) {
  const [privacy, setPrivacy] = useState(initial || {
    show_certificates: true, show_projects: true, show_badges: true,
    show_skills: true, show_goals: true, show_streak: true,
  });
  const [saving, setSaving] = useState(false);
  const toggle = (k) => setPrivacy((p) => ({ ...p, [k]: !p[k] }));
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/me/privacy", privacy);
      toast.success("Privacy updated");
      onSaved && onSaved();
    } finally { setSaving(false); }
  };
  const items = [
    { k: "show_certificates", label: "Certificates" },
    { k: "show_projects", label: "Projects" },
    { k: "show_badges", label: "Badges" },
    { k: "show_skills", label: "Skills mastered" },
    { k: "show_goals", label: "Long-term goal" },
    { k: "show_streak", label: "Streak" },
  ];
  return (
    <div className="rounded-xl border border-border bg-background/60 p-3">
      <div className="label-mono mb-2">// public portfolio privacy</div>
      <div className="grid grid-cols-2 gap-1">
        {items.map((it) => (
          <label key={it.k} className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={!!privacy[it.k]} onChange={() => toggle(it.k)} className="accent-primary" />
            {it.label}
          </label>
        ))}
      </div>
      <Button onClick={save} disabled={saving} size="sm" className="mt-3 rounded-full font-mono text-xs uppercase tracking-widest">
        {saving ? "Saving…" : "Save privacy"}
      </Button>
    </div>
  );
}

// Badge metadata for display
const BADGE_MAP = {
  first_lesson: { title: "First Steps", desc: "Completed first lesson." },
  streak_3: { title: "3-Day Streak", desc: "Learned 3 days in a row." },
  streak_7: { title: "Week Warrior", desc: "Learned 7 days in a row." },
  path_complete: { title: "Path Finisher", desc: "Finished a full path." },
  quiz_ace: { title: "Quiz Ace", desc: "Perfect quiz score." },
  coder: { title: "Coder", desc: "First AI code review." },
  builder: { title: "Builder", desc: "First project submitted." },
  level_5: { title: "Level 5", desc: "Reached level 5." },
  level_10: { title: "Level 10", desc: "Reached level 10." },
  assessment: { title: "Self-Aware", desc: "Completed an AI assessment." },
};

export default function PortfolioPage({ isPublic = false }) {
  const { slug } = useParams();
  const { user } = useAuth();
  const targetSlug = isPublic ? slug : user?.public_slug;
  const [pf, setPf] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [goals, setGoals] = useState("");
  const [savingGoals, setSavingGoals] = useState(false);

  useEffect(() => {
    if (!targetSlug) return;
    api.get(`/portfolio/${targetSlug}`).then((r) => {
      setPf(r.data);
      setGoals(r.data.long_term_goals || "");
    }).catch(() => setNotFound(true));
  }, [targetSlug]);

  const saveGoals = async () => {
    setSavingGoals(true);
    try {
      await api.put("/me/goals", { goals });
      toast.success("Long-term goal saved");
      const { data } = await api.get(`/portfolio/${targetSlug}`);
      setPf(data);
    } catch { toast.error("Could not save"); }
    finally { setSavingGoals(false); }
  };

  const publicUrl = targetSlug ? `${window.location.origin}/portfolio/${targetSlug}` : "";

  const copyLink = () => {
    navigator.clipboard.writeText(publicUrl);
    toast.success("Portfolio link copied");
  };

  if (notFound) {
    return (
      <div className="rounded-2xl border border-destructive/40 bg-destructive/10 p-8 text-center">
        <div className="font-display text-2xl font-black">Portfolio not found</div>
      </div>
    );
  }
  if (!pf) return <div className="label-mono animate-pulse">Loading portfolio…</div>;

  const content = (
    <div data-testid={PORTFOLIO.root} className="space-y-8 animate-fade-up">
      {isPublic && (
        <Link to="/" className="inline-flex items-center gap-2 text-xs font-mono text-muted-foreground hover:text-primary">
          <ArrowLeft className="h-3.5 w-3.5" /> LaneX Academy
        </Link>
      )}

      {/* Hero */}
      <div className="rounded-3xl border border-border bg-gradient-to-br from-primary/10 to-accent/5 p-6 md:p-10">
        <div className="grid gap-6 md:grid-cols-3">
          <div className="md:col-span-2">
            <VerifiedBadge />
            <h1 className="mt-4 font-display text-4xl font-black tracking-tighter sm:text-5xl">{pf.name}</h1>
            <div className="mt-2 label-mono">Learning with {pf.mentor_name || "LaneX"} · joined {pf.joined ? new Date(pf.joined).getFullYear() : "recently"}</div>
            {pf.long_term_goals && (
              <div className="mt-6 rounded-xl border border-border bg-background/60 p-4">
                <div className="label-mono text-primary">// long-term goal</div>
                <div className="mt-2 text-sm">{pf.long_term_goals}</div>
              </div>
            )}
            {!isPublic && (
              <div className="mt-4 space-y-3">
                <div>
                  <div className="label-mono">Set / update long-term goal</div>
                  <Textarea data-testid={PORTFOLIO.goalInput} value={goals} onChange={(e) => setGoals(e.target.value)} className="mt-2 min-h-[70px] rounded-lg" placeholder="e.g. Become a data engineer in 12 months." />
                  <Button data-testid={PORTFOLIO.goalSaveBtn} onClick={saveGoals} disabled={savingGoals || !goals.trim()} size="sm" className="mt-2 rounded-full font-mono text-xs uppercase tracking-widest">
                    <Sparkles className="mr-2 h-3.5 w-3.5" /> Save goal
                  </Button>
                </div>
                <PortfolioPrivacyControls
                  initial={pf.privacy || user?.portfolio_privacy}
                  onSaved={async () => { const { data } = await api.get(`/portfolio/${targetSlug}`); setPf(data); }}
                />
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl border border-border bg-background/60 p-4 text-center">
              <div className="label-mono">Level</div>
              <div className="mt-1 font-display text-4xl font-black">L{pf.level}</div>
              <Zap className="mx-auto mt-1 h-4 w-4 text-accent" />
            </div>
            <div className="rounded-2xl border border-border bg-background/60 p-4 text-center">
              <div className="label-mono">Streak</div>
              <div className="mt-1 font-display text-4xl font-black">{pf.streak}</div>
              <Flame className="mx-auto mt-1 h-4 w-4 text-accent" />
            </div>
            <div className="col-span-2 rounded-2xl border border-border bg-background/60 p-4 text-center">
              <div className="label-mono">XP</div>
              <div className="mt-1 font-display text-3xl font-black">{pf.xp}</div>
            </div>
          </div>
        </div>
        {!isPublic && (
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <Button data-testid={PORTFOLIO.shareBtn} onClick={copyLink} className="rounded-full btn-neon font-mono text-xs uppercase tracking-widest">
              <Share2 className="mr-2 h-4 w-4" /> Copy public link
            </Button>
            <a href={publicUrl} target="_blank" rel="noreferrer" className="rounded-full border border-border bg-background px-3 py-1.5 font-mono text-xs uppercase tracking-widest hover:border-primary">
              View public →
            </a>
          </div>
        )}
      </div>

      {/* Badges */}
      <section>
        <div className="label-mono mb-3">// badges</div>
        {(pf.badges || []).length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No badges yet — complete a lesson to unlock your first.</div>
        ) : (
          <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
            {(pf.badges || []).map((b) => {
              const meta = BADGE_MAP[b] || { title: b, desc: "" };
              return (
                <div key={b} className="rounded-2xl border border-accent/30 bg-accent/5 p-4 text-center">
                  <Trophy className="mx-auto h-6 w-6 text-accent" />
                  <div className="mt-2 font-display font-bold">{meta.title}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{meta.desc}</div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Skills mastered */}
      {(pf.skills_mastered || []).length > 0 && (
        <section>
          <div className="label-mono mb-3">// skills mastered</div>
          <div className="flex flex-wrap gap-2">
            {pf.skills_mastered.map((s) => (
              <span key={s} className="rounded-full border border-primary/40 bg-primary/10 px-4 py-1.5 font-mono text-xs text-primary">{s}</span>
            ))}
          </div>
        </section>
      )}

      {/* Certificates */}
      <section>
        <div className="label-mono mb-3">// certificates</div>
        {(pf.certificates || []).length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No certificates yet.</div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {pf.certificates.map((c) => (
              <div key={c.cert_id} className="rounded-2xl border border-border bg-card p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <VerifiedBadge />
                    <div className="mt-3 font-display text-lg font-bold">{c.path_title}</div>
                    <div className="mt-1 label-mono">{c.cert_id}</div>
                    <div className="mt-1 label-mono">issued {new Date(c.issued_at).toLocaleDateString()}</div>
                  </div>
                  <CertificateQR cert={c} size={72} />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Projects */}
      <section>
        <div className="label-mono mb-3">// projects</div>
        {(pf.projects || []).length === 0 ? (
          <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No projects submitted yet.</div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {pf.projects.map((p) => (
              <div key={p.project_id} className="rounded-2xl border border-border bg-card p-5">
                <div className="label-mono">{p.path_title}</div>
                <div className="mt-1 font-display text-lg font-bold">{p.spec?.title || "Project"}</div>
                <div className="mt-1 text-xs text-muted-foreground line-clamp-2">{p.spec?.description}</div>
                <div className="mt-3 flex items-center justify-between">
                  <span className="label-mono">Score</span>
                  <span className="font-display text-xl font-black">{p.review?.total ?? "—"}/100</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <footer className="border-t border-border pt-6">
        <div className="label-mono">Powered by LaneX Academy · verified portfolio</div>
      </footer>
    </div>
  );

  if (isPublic) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <header className="border-b border-border">
          <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-5">
            <Link to="/" className="flex items-center gap-2">
              <img src="/lanex-mark.png" alt="LaneX Academy" className="h-10 w-auto object-contain bg-white/95 rounded-lg p-1 shadow-md ring-1 ring-primary/30 drop-shadow-[0_0_14px_rgba(0,238,199,0.45)]" />
              <span className="font-display text-lg font-black">LaneX Academy</span>
            </Link>
            <div className="label-mono">// public portfolio</div>
          </div>
        </header>
        <main className="mx-auto max-w-4xl px-6 py-12">{content}</main>
      </div>
    );
  }
  return content;
}
